// Telemetry client — talks to Supabase's PostgREST endpoint directly, no SDK.
// Design constraints (see .claude/plans "Camada de observabilidade"):
//   - Zero work in useFrame; this module is only ever touched from phase
//     transitions and lifecycle boundaries.
//   - Events are written to the persisted offline queue BEFORE a send is
//     attempted, never after a failed attempt — so a teardown that kills the
//     attempt (e.g. the native exit intent) still leaves the event to drain
//     on next launch instead of losing it.
//   - Opt-out makes every function a no-op and clears the persisted queue.
//   - Missing build-time Supabase config (local dev with no env vars) is a
//     hard kill switch — the whole module goes inert, nothing throws.
import type { TelemetryEventName, TelemetryPropsFor } from "./events";
import { detectPlatform } from "./platform";

const INSTALL_ID_KEY = "sq_telemetryInstallId";
const OPT_OUT_KEY = "sq_telemetryOptOut";
const QUEUE_KEY = "sq_telemetryQueue";
const MAX_QUEUE_LENGTH = 200;

interface StoredEvent {
  install_id: string;
  session_id: string;
  app_version: string;
  build_sha: string;
  platform: string;
  name: TelemetryEventName;
  props: Record<string, unknown>;
  client_ts: string;
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, quota) — degrade to in-memory only.
  }
}

function safeRemoveItem(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore.
  }
}

function loadOrCreateInstallId(): string {
  const existing = safeGetItem(INSTALL_ID_KEY);
  if (existing) return existing;
  // Pure random UUID — never anything fingerprint-shaped. This is what keeps
  // the identifier out of the fingerprinting category in the store's data-use
  // declaration.
  const id = crypto.randomUUID();
  safeSetItem(INSTALL_ID_KEY, id);
  return id;
}

function loadOptOut(): boolean {
  return safeGetItem(OPT_OUT_KEY) === "1";
}

function loadQueue(): StoredEvent[] {
  const raw = safeGetItem(QUEUE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: StoredEvent[]) {
  const trimmed =
    queue.length > MAX_QUEUE_LENGTH
      ? queue.slice(queue.length - MAX_QUEUE_LENGTH)
      : queue;
  safeSetItem(QUEUE_KEY, JSON.stringify(trimmed));
}

const hadInstallId = safeGetItem(INSTALL_ID_KEY) != null;
const installId = loadOrCreateInstallId();
export const isFirstLaunch = !hadInstallId;
const sessionId = crypto.randomUUID();
let optedOut = loadOptOut();
let queue: StoredEvent[] = loadQueue();
let flushing = false;

// Build-time kill switch: no Supabase project configured (local dev with no
// env vars exported) → the whole module goes inert.
const configured = Boolean(__SUPABASE_URL__ && __SUPABASE_ANON_KEY__);

function isEnabled(): boolean {
  return configured && !optedOut;
}

function eventsEndpoint(): string {
  return `${__SUPABASE_URL__}/rest/v1/events`;
}

export function track<Name extends TelemetryEventName>(
  name: Name,
  props: TelemetryPropsFor<Name>,
) {
  if (!isEnabled()) return;

  const event: StoredEvent = {
    install_id: installId,
    session_id: sessionId,
    app_version: __APP_VERSION__,
    build_sha: __BUILD_SHA__,
    platform: detectPlatform(),
    name,
    props: props as unknown as Record<string, unknown>,
    client_ts: new Date().toISOString(),
  };

  // Persist first — see module doc comment.
  queue = [...queue, event];
  saveQueue(queue);
  void flush();
}

export async function flush(): Promise<void> {
  if (!isEnabled() || flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue;
  try {
    const response = await fetch(eventsEndpoint(), {
      method: "POST",
      keepalive: true,
      headers: {
        apikey: __SUPABASE_ANON_KEY__,
        Authorization: `Bearer ${__SUPABASE_ANON_KEY__}`,
        "Content-Type": "application/json",
        Prefer: "return=minimal",
      },
      body: JSON.stringify(batch),
    });
    if (response.ok) {
      // Only drop the events actually sent — track() may have appended more
      // to `queue` while this request was in flight.
      queue = queue.slice(batch.length);
      saveQueue(queue);
    }
  } catch {
    // Offline or request failed — batch stays queued, already persisted.
    // Drained on the next natural flush point or next launch.
  } finally {
    flushing = false;
  }
}

// Best-effort last gasp on hard teardown (pagehide). sendBeacon can't carry
// custom headers, so auth rides the query string — Supabase's gateway
// accepts `apikey` there. Unverified on-device; if it silently fails the
// persisted queue still drains on next launch, so nothing is lost outright.
export function flushBeacon() {
  if (!isEnabled() || queue.length === 0 || !("sendBeacon" in navigator))
    return;
  const url = `${eventsEndpoint()}?apikey=${encodeURIComponent(__SUPABASE_ANON_KEY__)}`;
  const blob = new Blob([JSON.stringify(queue)], {
    type: "application/json",
  });
  const sent = navigator.sendBeacon(url, blob);
  if (sent) {
    queue = [];
    saveQueue(queue);
  }
}

export function setTelemetryOptOut(value: boolean) {
  optedOut = value;
  safeSetItem(OPT_OUT_KEY, value ? "1" : "0");
  if (value) {
    queue = [];
    safeRemoveItem(QUEUE_KEY);
  }
}

export function getTelemetryOptOut(): boolean {
  return optedOut;
}

export function getInstallId(): string {
  return installId;
}

// Debug handle for remote DevTools — same precedent as window.__sq in
// index.tsx: the installed Quest TWA has no visible console.
(
  window as unknown as {
    __sqTelemetry?: {
      queue: () => StoredEvent[];
      flush: () => Promise<void>;
      installId: string;
      enabled: () => boolean;
    };
  }
).__sqTelemetry = {
  queue: () => queue,
  flush,
  installId,
  enabled: isEnabled,
};
